/**
 * Date-range preset utilities for the Analytics dashboard.
 *
 * Presets are persisted by IDENTITY (e.g. "last_7_days"), not by absolute dates,
 * so that "Last 7 days" always resolves relative to *now* on every page load.
 * The exception is CUSTOM, whose absolute from/to are persisted verbatim.
 *
 * All ranges are half-open on the "to" side conceptually, but we return an
 * inclusive-feeling [from, to] pair as ISO strings that the backend interprets
 * against the community timezone + day_start_hour.
 */

export const PRESET_IDS = {
  TODAY: 'today',
  YESTERDAY: 'yesterday',
  LAST_7_DAYS: 'last_7_days',
  LAST_30_DAYS: 'last_30_days',
  THIS_MONTH: 'this_month',
  LAST_MONTH: 'last_month',
  LAST_3_MONTHS: 'last_3_months',
  LAST_6_MONTHS: 'last_6_months',
  LAST_YEAR: 'last_year',
  ALL_TIME: 'all_time',
  CUSTOM: 'custom'
}

/**
 * Ordered list of presets for rendering in the UI.
 * `labelKey` is an i18n key under pages.analytics.presets.*
 */
export const RANGE_PRESETS = [
  { id: PRESET_IDS.TODAY, labelKey: 'pages.analytics.presets.today' },
  { id: PRESET_IDS.YESTERDAY, labelKey: 'pages.analytics.presets.yesterday' },
  { id: PRESET_IDS.LAST_7_DAYS, labelKey: 'pages.analytics.presets.last_7_days' },
  { id: PRESET_IDS.LAST_30_DAYS, labelKey: 'pages.analytics.presets.last_30_days' },
  { id: PRESET_IDS.THIS_MONTH, labelKey: 'pages.analytics.presets.this_month' },
  { id: PRESET_IDS.LAST_MONTH, labelKey: 'pages.analytics.presets.last_month' },
  { id: PRESET_IDS.LAST_3_MONTHS, labelKey: 'pages.analytics.presets.last_3_months' },
  { id: PRESET_IDS.LAST_6_MONTHS, labelKey: 'pages.analytics.presets.last_6_months' },
  { id: PRESET_IDS.LAST_YEAR, labelKey: 'pages.analytics.presets.last_year' },
  { id: PRESET_IDS.ALL_TIME, labelKey: 'pages.analytics.presets.all_time' },
  { id: PRESET_IDS.CUSTOM, labelKey: 'pages.analytics.presets.custom' }
]

/**
 * Resolve a preset id into concrete { from: Date, to: Date }.
 * For CUSTOM the caller-supplied customFrom/customTo (ISO strings or Dates) are used.
 *
 * dayStartHour (the community's "business day start") shifts every day
 * boundary: with dayStartHour=10, "today" runs 10:00 -> 09:59:59 tomorrow, and
 * "this month" starts on the 1st at 10:00. This keeps preset edges aligned
 * with the backend's bucket boundaries — otherwise a "this month" range at
 * month granularity leaks a sliver of the previous month's bucket.
 *
 * @param {string} presetId
 * @param {{ customFrom?: string|Date, customTo?: string|Date, dayStartHour?: number }} [options]
 * @returns {{ from: Date, to: Date }}
 */
export function resolvePresetRange(presetId, { customFrom, customTo, dayStartHour = 0 } = {}) {
  const dsh = Math.min(23, Math.max(0, Number(dayStartHour) || 0))
  const now = new Date()
  // The calendar frame of the current BUSINESS day: at 08:00 with dsh=10 the
  // business day (and month) is still yesterday's.
  const anchor = new Date(now.getTime() - dsh * 3600000)

  const dayStart = (date) => {
    const d = new Date(date)
    d.setHours(dsh, 0, 0, 0)
    return d
  }
  // Inclusive end: one second before the next business day begins.
  const dayEnd = (date) => {
    const d = new Date(date)
    d.setDate(d.getDate() + 1)
    d.setHours(dsh, 0, 0, 0)
    return new Date(d.getTime() - 1000)
  }
  const daysBack = (n) => {
    const d = new Date(anchor)
    d.setDate(d.getDate() - n)
    return d
  }

  switch (presetId) {
    case PRESET_IDS.TODAY:
      return { from: dayStart(anchor), to: dayEnd(anchor) }

    case PRESET_IDS.YESTERDAY:
      return { from: dayStart(daysBack(1)), to: dayEnd(daysBack(1)) }

    case PRESET_IDS.LAST_7_DAYS:
      return { from: dayStart(daysBack(6)), to: dayEnd(anchor) }

    case PRESET_IDS.LAST_30_DAYS:
      return { from: dayStart(daysBack(29)), to: dayEnd(anchor) }

    case PRESET_IDS.THIS_MONTH:
      return { from: dayStart(new Date(anchor.getFullYear(), anchor.getMonth(), 1)), to: dayEnd(anchor) }

    case PRESET_IDS.LAST_MONTH: {
      const from = new Date(anchor.getFullYear(), anchor.getMonth() - 1, 1)
      const to = new Date(anchor.getFullYear(), anchor.getMonth(), 0) // last day of prev month
      return { from: dayStart(from), to: dayEnd(to) }
    }

    case PRESET_IDS.LAST_3_MONTHS: {
      const from = new Date(anchor)
      from.setMonth(from.getMonth() - 3)
      return { from: dayStart(from), to: dayEnd(anchor) }
    }

    case PRESET_IDS.LAST_6_MONTHS: {
      const from = new Date(anchor)
      from.setMonth(from.getMonth() - 6)
      return { from: dayStart(from), to: dayEnd(anchor) }
    }

    case PRESET_IDS.LAST_YEAR: {
      const from = new Date(anchor)
      from.setFullYear(from.getFullYear() - 1)
      return { from: dayStart(from), to: dayEnd(anchor) }
    }

    case PRESET_IDS.ALL_TIME:
      // Bounded "all time": the backend caps month granularity at 1827 days
      // (~5 years) — older data predates the product, so this is effectively
      // everything.
      return { from: dayStart(daysBack(MONTH_MAX_DAYS - 1)), to: dayEnd(anchor) }

    case PRESET_IDS.CUSTOM: {
      const from = customFrom ? new Date(customFrom) : dayStart(anchor)
      const to = customTo ? new Date(customTo) : dayEnd(anchor)
      return { from, to }
    }

    default:
      // Fall back to last 7 days for unknown presets.
      return resolvePresetRange(PRESET_IDS.LAST_7_DAYS, { dayStartHour: dsh })
  }
}

/**
 * Format a Date as a naive local ISO string (no timezone designator),
 * e.g. 2026-07-05T00:00:00.
 *
 * The backend reinterprets these wall-clock values in the community timezone
 * (same convention as the existing orders pages, which send naive dates).
 * Date.toISOString() must NOT be used for API range params — it converts the
 * wall-clock to UTC, which the backend then re-shifts as if it were local,
 * skewing the range by the browser's UTC offset (e.g. "today" showing
 * yesterday's bucket).
 */
export function toNaiveLocalIso(date) {
  const d = new Date(date)
  const pad = (n) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
}

/**
 * The comparison window immediately preceding [from, to]: same duration,
 * ending exactly where the current range starts. Used for KPI deltas
 * ("vs. previous period").
 */
export function previousRange(from, to) {
  const f = new Date(from).getTime()
  const t = new Date(to).getTime()
  const duration = Math.max(1, t - f)
  return { from: new Date(f - duration), to: new Date(f) }
}

/** Number of whole days spanned by a resolved range (inclusive). */
export function rangeDurationDays(from, to) {
  const MS_PER_DAY = 24 * 60 * 60 * 1000
  return Math.max(1, Math.ceil((new Date(to).getTime() - new Date(from).getTime()) / MS_PER_DAY))
}

/**
 * Granularity options and the constraint that hourly is only available for
 * short ranges (<= 14 days) and daily up to 400 days.
 */
export const GRANULARITIES = {
  HOUR: 'hour',
  DAY: 'day',
  WEEK: 'week',
  MONTH: 'month'
}

export const HOUR_MAX_DAYS = 14
export const DAY_MAX_DAYS = 400
export const WEEK_MAX_DAYS = 731
export const MONTH_MAX_DAYS = 1827

/**
 * Given a range duration in days, return the list of allowed granularity ids.
 * Caps mirror the backend's range guards per granularity.
 * @param {number} durationDays
 * @returns {string[]}
 */
export function allowedGranularities(durationDays) {
  const allowed = []
  if (durationDays <= HOUR_MAX_DAYS) allowed.push(GRANULARITIES.HOUR)
  if (durationDays <= DAY_MAX_DAYS) allowed.push(GRANULARITIES.DAY)
  if (durationDays <= WEEK_MAX_DAYS) allowed.push(GRANULARITIES.WEEK)
  allowed.push(GRANULARITIES.MONTH)
  return allowed
}

/**
 * Coerce a requested granularity into an allowed one for the given duration.
 * Falls back to the finest allowed granularity when the request is invalid.
 * @param {string} requested
 * @param {number} durationDays
 * @returns {string}
 */
export function constrainGranularity(requested, durationDays) {
  const allowed = allowedGranularities(durationDays)
  if (allowed.includes(requested)) return requested
  return allowed[0]
}
